import { useEffect, type CSSProperties } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CheckboxRaw } from '@/shared/ui/components/form/inputs';
import Select from '@/shared/ui/components/form/inputs/Select';
import { Text } from '@/shared/ui';
import { shouldShowParkingType } from '@/shared/utils';
import { MaskedIntegerField } from './MaskedNumberField';
import {
  FURNISHED_OPTIONS,
  FACING_OPTIONS,
  PARKING_TYPE_OPTIONS,
  PROPERTY_FIELD_LIMITS,
} from './schema';
import type { PropertyFormValues } from './schema';

interface CharacteristicsExtraFieldsProps {
  form: UseFormReturn<PropertyFormValues>;
  isApartment: boolean;
  isLand: boolean;
}

const checkboxLabelStyle: CSSProperties = {
  fontSize: 'var(--text-small)',
  fontFamily: 'var(--font-family)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
};

/**
 * Clears parking_type when the listing has no parking spots.
 *
 * @param form - Property drawer form instance.
 * @param parkingSpots - Current parking spot count from the form.
 */
function useClearParkingTypeWithoutSpots(
  form: UseFormReturn<PropertyFormValues>,
  parkingSpots: number | null | undefined,
): void {
  const { setValue, getValues } = form;
  useEffect(() => {
    if (shouldShowParkingType(parkingSpots)) return;
    if (getValues('parking_type') == null) return;
    setValue('parking_type', undefined);
  }, [parkingSpots, setValue, getValues]);
}

/**
 * Apartment floor/balcony flags plus room count, year built, and select fields
 * shared by non-land property categories. Parking type appears only when spots > 0.
 */
export function CharacteristicsExtraFields({ form, isApartment, isLand }: CharacteristicsExtraFieldsProps) {
  const { t } = useTranslation('features/properties');
  const errors = form.formState.errors;
  const parkingSpots = form.watch('parking_spots');
  const showParkingType = shouldShowParkingType(parkingSpots);
  useClearParkingTypeWithoutSpots(form, parkingSpots);

  return (
    <>
      {isApartment && (
        <div className="flex flex-col gap-(--space-2-5) mt-(--space-4)">
          <Controller
            name="floor"
            control={form.control}
            render={({ field }) => (
              <MaskedIntegerField
                label={t('form.drawer.floor')}
                error={errors.floor?.message}
                maxDigits={PROPERTY_FIELD_LIMITS.floor.maxDigits}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="balcony"
            control={form.control}
            render={({ field }) => (
              <Text as="label" className="flex items-center gap-(--space-2)" style={checkboxLabelStyle}>
                <CheckboxRaw value={!!field.value} onToggle={v => field.onChange(v)} />
                {t('form.drawer.balcony')}
              </Text>
            )}
          />
          <Controller
            name="gourmet_balcony"
            control={form.control}
            render={({ field }) => (
              <Text as="label" className="flex items-center gap-(--space-2)" style={checkboxLabelStyle}>
                <CheckboxRaw value={!!field.value} onToggle={v => field.onChange(v)} />
                {t('form.drawer.gourmetBalcony')}
              </Text>
            )}
          />
        </div>
      )}

      {!isLand && (
        <div className="flex flex-col gap-(--space-3) mt-(--space-4)">
          <Controller
            name="total_rooms"
            control={form.control}
            render={({ field }) => (
              <MaskedIntegerField
                label={t('form.drawer.totalRooms')}
                error={errors.total_rooms?.message}
                maxDigits={PROPERTY_FIELD_LIMITS.totalRooms.maxDigits}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="year_built"
            control={form.control}
            render={({ field }) => (
              <MaskedIntegerField
                label={t('form.drawer.yearBuilt')}
                error={errors.year_built?.message}
                maxDigits={PROPERTY_FIELD_LIMITS.yearBuilt.maxDigits}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="furnished"
            control={form.control}
            render={({ field }) => (
              <Select
                label={t('form.drawer.furnished')}
                placeholder={t('form.drawer.selectPlaceholder')}
                value={field.value ?? ''}
                onChange={field.onChange}
                data={(FURNISHED_OPTIONS as readonly string[]).map(o => ({
                  value: o,
                  label: t(`form.options.furnished.${o}`),
                }))}
              />
            )}
          />
          <Controller
            name="facing"
            control={form.control}
            render={({ field }) => (
              <Select
                label={t('form.drawer.facing')}
                placeholder={t('form.drawer.selectPlaceholder')}
                value={field.value ?? ''}
                onChange={field.onChange}
                data={(FACING_OPTIONS as readonly string[]).map(o => ({
                  value: o,
                  label: t(`form.options.facing.${o}`),
                }))}
              />
            )}
          />
          {showParkingType && (
            <Controller
              name="parking_type"
              control={form.control}
              render={({ field }) => (
                <Select
                  label={t('form.drawer.parkingType')}
                  placeholder={t('form.drawer.selectPlaceholder')}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  data={(PARKING_TYPE_OPTIONS as readonly string[]).map(o => ({
                    value: o,
                    label: t(`form.options.parkingType.${o}`),
                  }))}
                />
              )}
            />
          )}
          <Controller
            name="accepts_pets"
            control={form.control}
            render={({ field }) => (
              <Text as="label" className="flex items-center gap-(--space-2)" style={checkboxLabelStyle}>
                <CheckboxRaw value={!!field.value} onToggle={v => field.onChange(v)} />
                {t('form.drawer.acceptsPets')}
              </Text>
            )}
          />
        </div>
      )}
    </>
  );
}
